<template>
    <!-- Div to help align form -->
    <div class="align-center-helper">
        <divider-generic
        icon="person"
        :applyBorder="false"
        :title="t('approvals.operators.edit.step1.dataDivider')"/>
        <!-- Username Output -->
        <text-block-generic
        :params="{ label: t('approvals.operators.edit.step1.accessionNumberLabel'), content: operatorData.username.value }"/>
        <!-- Name Input -->
        <input-generic
        id="name"
        :params="nameInputParams"
        :inputValue="operatorData.name.value"
        @input="emit('updateData', { param: 'name', value: $event})"
        @onBlur="nameInputValidation"/>
        <!-- E-mail Input -->
        <input-generic
        id="email"
        :params="emailInputParams"
        :inputValue="operatorData.email.value"
        @input="emit('updateData', { param: 'email', value: $event.trim()})"
        @onBlur="emailInputValidation"/>
        <!-- Phone Number Input -->
        <input-generic
        :params="phoneInputParams"
        :inputValue="operatorData.msisdn.value"
        @input="emit('updateData', { param: 'msisdn', value: $event})"
        @onBlur="phoneInputValidation"/>
        <!-- Nif Input -->
        <input-generic
        id="msisdn"
        :params="nifInputParams"
        :inputValue="operatorData.nif.value"
        @input="emit('updateData', { param: 'nif', value: $event})"/>
        <!-- New Accounts -->
        <selection-controller-radio-group
        id="accessToNewAccounts"
        mandatory
        :label="t('approvals.operators.edit.step1.radiosLabel')"
        :radioParams="radioParamsData"
        :errorState="operatorData.accessToNewAccounts.errorState"
        :errorMessage="t('mandatoryFieldError')"
        @clicked="emit('updateData', { param: 'accessToNewAccounts', value: radioParamsData.find(ele => ele.id === $event).value})"/>
        <div class="profile-elements-wrapper">
            <!-- Profile -->
            <dropdown-generic
            id="profileId"
            :params="profileParams"
            :options="getProfilesForDropdown"
            :selectedOption="operatorData.profileId.value"
            @clicked="changingProfileHandler"/>
            <!-- Button to open profile modal -->
            <button-secondary
            icon="info"
            :label="t('approvals.operators.edit.step1.profilesButton')"
            @clicked="emit('showProfilesModal')"/>
        </div>

        <!-- Weight Input -->
        <input-generic
        id="weight"
        v-if="operatorData.profileId.value === 1"
        :params="weightInputParams"
        :inputValue="operatorData.weight.value"
        @input="emit('updateData', { param: 'weight', value: $event})"/>
        <!-- Accounts Divider -->
        <divider-generic v-if="operatorData.profileId.value" icon="lock" :title="t('approvals.operators.edit.step1.accountsDivider')"/>
    </div>
    <div v-if="operatorData.profileId.value" id="accountIds" class="add-accounts-wrapper">
        <account-block
        v-for="(account, index) in accounts"
        :key="account.id"
        :account="account"
        :operatorData="operatorData"
        :cantRemove="props.accounts.length === 1"
        :selectedProfileId="operatorData.profileId.value"
        @accountSelected="emit('accountSelected', { value: $event, index: index})"
        @addOperationLimits="emit('addOperationLimits', index)"
        @defaultAccount="emit('updateData', { param: 'defaultAccountId', value: $event})"
        @operationType="emit('operationType', { value: $event, index: index})"
        @operationLimits="emit('operationLimits', { value: $event, index: index})"
        @deleteOperationRow="emit('deleteOperationRow', { value: $event, index: index })"
        @duplicateAccountBlock="emit('duplicateAccountBlock',  index)"
        @resetAccountValues="emit('resetAccountValues', index)"
        @resetRowValues="emit('resetRowValues', { value: $event, index: index })"
        @removeAccountBlock="[tempParams = index, showConfirmModal = true]"
        @accountBlockFinishUpdate="emit('accountBlockFinishUpdate')"/>
        <!-- Add more accounts button -->
        <div v-if="accounts.length < getAccounts.bankAccountView.length" class="button-wrapper">
            <button-secondary
            icon="plus"
            buttonSize="small"
            :label="t('approvals.operators.edit.step1.addAccountButton')"
            @clicked="emit('addAccounts')"/>
        </div>
    </div>
    <!-- Remove Account Warning modal -->
    <modal-centered-generic
    v-if="showConfirmModal"
    negative
    :title="t('approvals.operators.edit.step1.removeModal.title')"
    :text="t('approvals.operators.edit.step1.removeModal.text')"
    :leftButtonText="t('approvals.operators.edit.step1.removeModal.leftButton')"
    :rightButtonText="t('approvals.operators.edit.step1.removeModal.rightButton')"
    @hideMe="showConfirmModal = false"
    @rightButtonClicked="[emit('removeAccountBlock', tempParams), showConfirmModal = false]"/>
    <!-- Changing Profile Warning modal -->
    <modal-centered-generic
    v-if="showProfileChangeAlertModal"
    negative
    :title="t('approvals.operators.edit.step1.changeProfileModal.title')"
    :text="profileModalText"
    :leftButtonText="t('approvals.operators.edit.step1.changeProfileModal.leftButton')"
    :rightButtonText="t('approvals.operators.edit.step1.changeProfileModal.rightButton')"
    @hideMe="showProfileChangeAlertModal = false"
    @rightButtonClicked="[emit('updateData', { param: 'profileId' , value: tempProfile}), showProfileChangeAlertModal = false]"/>
</template>

<script setup>
import DividerGeneric from '@/components/generic/dividers/Generic'
import TextBlockGeneric from '@/components/generic/textBlocks/Generic'
import InputGeneric from '@/components/generic/inputs/Generic'
import SelectionControllerRadioGroup from '@/components/generic/selectors/radio/RadioGroup'
import DropdownGeneric from '@/components/generic/dropdowns/generic/Generic'
import ButtonSecondary from '@/components/generic/buttons/Secondary'
import AccountBlock from '@/components/features/approvals/operators/AccountBlock'
import ModalCenteredGeneric from '@/components/generic/modals/centered/Generic'

/* Vue & Assets */
import { computed, ref } from 'vue'
import { NUMBERS_ONLY, PHONE_NUMBER } from '@/assets/constants/masks'
import { percentageValidator, emailValidator, phoneNumberValidator } from '@/assets/scripts/validators'

/* Store */
import { mapGetters } from '@/store/helpers/mappers'
import { moduleMapper } from '@/store/helpers/modules.map'

/* i18n */
import { useI18n } from 'vue-i18n'
const { t } = useI18n()

/* mapActions & mapGetters */
const { getAccounts, getProfilesForDropdown } = mapGetters(moduleMapper.APPROVALS_OPERATORS)

/* Emmitters */
const emit = defineEmits(['showProfilesModal', 'updateData', 'addAccounts', 'accountSelected', 'addOperationLimits', 'operationType', 'operationLimits',
                        'deleteOperationRow', 'duplicateAccountBlock', 'removeAccountBlock', 'fieldHasError', 'resetAccountValues', 'resetRowValues', 'accountBlockFinishUpdate'])

const props = defineProps({
    operatorData: Object,
    accounts: Array
})
/*************************************************
*                                                *
*                      Data                      *
*                                                *
*************************************************/
const showConfirmModal = ref(false)
const showProfileChangeAlertModal = ref(false)
const tempParams = ref({})
const tempProfile = ref(null)

const nameInputParams = computed(() => {
    return {
        labelText: t('approvals.operators.edit.step1.nameLabel'),
        placeholder: t('approvals.operators.edit.step1.namePlaceholder'),
        bottomMessage: t('approvals.operators.edit.step1.nameBottomMessage'),
        errorState: props.operatorData.name.errorState || props.operatorData.name.fieldHasError,
        errorMessage: props.operatorData.name.errorState ? t('mandatoryFieldError') : t('approvals.operators.edit.step1.nameBottomMessage'),
        mandatory: true
    }
})

const nameInputValidation = () => {
    /* [ourString].trim().indexOf(' ') === -1 checks if we have 2 or more words separated by a empty space */
    if (!!props.operatorData.name.value.length && props.operatorData.name.value.trim().indexOf(' ') === -1) {
        emit('fieldHasError', 'name')
    }
}

const emailInputParams = computed(() => {
    return {
        labelText: t('approvals.operators.edit.step1.emailLabel'),
        placeholder: t('approvals.operators.edit.step1.emailPlaceholder'),
        mandatory: true,
        errorState: props.operatorData.email.errorState || props.operatorData.email.fieldHasError,
        errorMessage: props.operatorData.email.errorState ? t('mandatoryFieldError') : t('approvals.operators.edit.step1.emailErrorMessage')
    }
})

const emailInputValidation = () => {
    if (!!props.operatorData.email.value.length && !emailValidator(props.operatorData.email.value)) {
        emit('fieldHasError', 'email')
    }
}

const phoneInputParams = computed(() => {
    return {
        labelText: t('approvals.operators.edit.step1.phoneLabel'),
        placeholder: t('approvals.operators.edit.step1.phonePlaceholder'),
        mandatory: true,
        mask: PHONE_NUMBER,
        errorState: props.operatorData.msisdn.errorState || props.operatorData.msisdn.fieldHasError,
        errorMessage: props.operatorData.msisdn.errorState ? t('mandatoryFieldError') : t('approvals.operators.edit.step1.phoneErrorMessage')
    }
})

const phoneInputValidation = () => {
    if (!!props.operatorData.msisdn.value.length && !phoneNumberValidator(props.operatorData.msisdn.value)) {
        emit('fieldHasError', 'msisdn')
    }
}

const nifInputParams = computed(() => {
    return {
        labelText: t('approvals.operators.edit.step1.nifLabel'),
        placeholder: t('approvals.operators.edit.step1.nifPlaceholder'),
        maxLength: 15,
        errorState: props.operatorData.nif.errorState,
        errorMessage: t('mandatoryFieldError')
    }
})

const radioParamsData = computed(() => {
    return [{
        id: 'a',
        text: t('approvals.operators.edit.step1.radioYes'),
        value: true,
        selected: props.operatorData.accessToNewAccounts.value === true
    }, {
        id: 'b',
        text: t('approvals.operators.edit.step1.radioNo'),
        value: false,
        selected: props.operatorData.accessToNewAccounts.value === false
    }]
})

const profileParams = computed(() => {
    return {
        labelText: t('approvals.operators.edit.step1.profileLabel'),
        placeholder: t('approvals.operators.edit.step1.profilePlaceholder'),
        mandatory: true,
        errorState: props.operatorData.profileId.errorState,
        errorMessage: t('mandatoryFieldError')
    }
})

const profileModalText = ref('')
const changingProfileHandler = $event => {
    if (!props.operatorData.profileId.value || props.operatorData.profileId.value === 4) {
        emit('updateData', { param: 'profileId', value: $event })
    } else {
        tempProfile.value = $event
        showProfileChangeAlertModal.value = true

        if (tempProfile.value === 4) {
            profileModalText.value = t('approvals.operators.edit.step1.changeProfileModal.text', { profile: getProfilesForDropdown.value.find(obj => obj.id === tempProfile.value).operatorProfileDescription })
        } else if (tempProfile.value === 2 || tempProfile.value === 1) {
            profileModalText.value = t('approvals.operators.edit.step1.changeProfileModal.secondText', { profile: getProfilesForDropdown.value.find(obj => obj.id === tempProfile.value).operatorProfileDescription })
        }
    }
}

const weightInputParams = computed(() => {
    return {
        labelText: t('approvals.operators.edit.step1.weightLabel'),
        placeholder: t('approvals.operators.edit.step1.weightPlaceholder'),
        mandatory: true,
        auxiliarRightText: '%',
        mask: NUMBERS_ONLY,
        ourMask: {
            validatorFn: value => { return percentageValidator(value, 100) }
        },
        bottomMessage: t('approvals.operators.edit.step1.weightBottomMessage'),
        errorState: props.operatorData.weight.errorState && props.operatorData.profileId.value === 1,
        errorMessage: t('mandatoryFieldError')
    }
})
</script>

<style lang="scss" scoped>
.align-center-helper {
    width: 508px;
    max-width: 100%;

    display: flex;
    flex-direction: column;
    gap: 24px;
}

.add-accounts-wrapper {
    width: 100%;

    display: flex;
    flex-direction: column;
    gap: 16px;

    .button-wrapper {
        width: 100%;
        height: 64px;

        display: flex;
        align-items: center;
        justify-content: center;

        background-color: var(--secondary-light-3);
        border: 2px dashed var(--secondary-light-2);
        border-radius: 8px;
    }
}
</style>
